import { forwardRef, type CSSProperties } from 'react';
import type {
  SprintReportData,
} from '../../features/sprintboard/report/sprintReportModel';

/**
 * SprintReportDocument — a dedicated, PRINT-FRIENDLY one-page layout for the
 * sprint summary report. It is intentionally styled with LIGHT, legible inline
 * styles (not the app's dark Tailwind theme) so it rasterizes cleanly to a PDF
 * via html2canvas and prints well on paper.
 *
 * It is rendered off-screen (see {@link SprintReportButton}) at a fixed A4-ish
 * width (794px ≈ A4 @ 96dpi) and kept compact so the whole thing fits on a
 * single page. The forwarded ref points at the outer page node the exporter
 * rasterizes.
 */

const INK = '#111827';
const MUTED = '#6b7280';
const LINE = '#e5e7eb';
const ACCENT = '#4f46e5';
const GOOD = '#047857';
const BAD = '#b91c1c';
const AMBER = '#b45309';

const page: CSSProperties = {
  width: 794,
  minHeight: 1123,
  boxSizing: 'border-box',
  padding: '40px 44px',
  background: '#ffffff',
  color: INK,
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSize: 13,
  lineHeight: 1.5,
};

const h1: CSSProperties = { fontSize: 24, fontWeight: 700, margin: 0 };
const sectionTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: MUTED,
  margin: '0 0 8px',
};
const card: CSSProperties = {
  border: `1px solid ${LINE}`,
  borderRadius: 10,
  padding: '12px 14px',
};

function Pill({ text, tone }: { text: string; tone: 'good' | 'bad' | 'neutral' }) {
  const bg = tone === 'good' ? '#ecfdf5' : tone === 'bad' ? '#fef2f2' : '#f3f4f6';
  const fg = tone === 'good' ? GOOD : tone === 'bad' ? BAD : MUTED;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {text}
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  const color = tone === 'good' ? GOOD : tone === 'bad' ? BAD : INK;
  return (
    <div style={{ ...card, flex: '1 1 0', minWidth: 0 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{label}</div>
    </div>
  );
}

export interface SprintReportDocumentProps {
  data: SprintReportData;
}

export const SprintReportDocument = forwardRef<
  HTMLDivElement,
  SprintReportDocumentProps
>(function SprintReportDocument({ data }, ref) {
  const capacityTone =
    data.remainingCapacityDays < data.remainingEffortDays ? 'bad' : undefined;

  return (
    <div ref={ref} style={page}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          borderBottom: `2px solid ${INK}`,
          paddingBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: ACCENT, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Sprint Summary Report
          </div>
          <h1 style={h1}>{data.sprintName}</h1>
          {data.goal && (
            <div style={{ color: MUTED, marginTop: 4, fontSize: 13 }}>
              🎯 {data.goal}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ marginBottom: 6 }}>
            <Pill text={data.healthLabel} tone={data.healthy ? 'good' : 'bad'} />
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>{data.statusLabel}</div>
          <div style={{ fontSize: 12, color: MUTED }}>{data.dayLabel}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ margin: '18px 0 6px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            flex: 1,
            height: 12,
            borderRadius: 999,
            background: '#eef2ff',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.max(0, Math.min(100, data.percentComplete))}%`,
              height: '100%',
              background: ACCENT,
            }}
          />
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, minWidth: 44, textAlign: 'right' }}>
          {data.percentComplete}%
        </div>
      </div>
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 16 }}>
        {data.completedEffortDays}d of {data.committedEffortDays}d committed effort
        complete
      </div>

      {/* Key stats */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        <Stat label="Committed" value={`${data.committedEffortDays}d`} />
        <Stat label="Completed" value={`${data.completedEffortDays}d`} tone="good" />
        <Stat label="Remaining" value={`${data.remainingEffortDays}d`} />
        <Stat
          label="Remaining capacity"
          value={`${data.remainingCapacityDays}d`}
          tone={capacityTone}
        />
        <Stat
          label={`Blocked${data.atRiskCount > 0 ? ` · ${data.atRiskCount} at risk` : ''}`}
          value={String(data.blockedCount)}
          tone={data.blockedCount > 0 ? 'bad' : undefined}
        />
      </div>

      {/* Two-column body */}
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <section style={{ marginBottom: 16 }}>
            <h2 style={sectionTitle}>Blockers</h2>
            {data.blockers.length === 0 ? (
              <div style={{ color: MUTED, fontSize: 12 }}>No blocked tasks. 🎉</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                {data.blockers.map((b, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '6px 0',
                      borderBottom: `1px solid ${LINE}`,
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>{b.title}</span>
                      {b.reason && (
                        <span style={{ color: MUTED }}> — {b.reason}</span>
                      )}
                    </span>
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color: b.days >= 2 ? BAD : AMBER,
                        fontWeight: 600,
                      }}
                    >
                      {b.days}d
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 style={sectionTitle}>Scope Added (Unplanned)</h2>
            {data.unplannedCount === 0 ? (
              <div style={{ color: MUTED, fontSize: 12 }}>
                No unplanned scope added mid-sprint.
              </div>
            ) : (
              <div style={{ ...card, background: '#fffbeb', borderColor: '#fde68a' }}>
                <span style={{ fontWeight: 700, color: AMBER }}>
                  {data.unplannedCount} task{data.unplannedCount === 1 ? '' : 's'}
                </span>{' '}
                pulled in mid-sprint
                {data.unplannedEffortDays > 0 && (
                  <>
                    {' '}
                    (<b>{data.unplannedEffortDays}d</b> of added effort)
                  </>
                )}
                .
              </div>
            )}
          </section>
        </div>

        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <section style={{ marginBottom: 16 }}>
            <h2 style={sectionTitle}>Top Goals</h2>
            {data.topGoals.length === 0 ? (
              <div style={{ color: MUTED, fontSize: 12 }}>
                No goals linked to this sprint's work.
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                {data.topGoals.map((g, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '6px 0',
                      borderBottom: `1px solid ${LINE}`,
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>{g.name}</span>
                      <span style={{ color: MUTED }}> · {g.statusLabel}</span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {g.percentComplete}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 style={sectionTitle}>Standup Highlights</h2>
            {data.standupHighlights.length === 0 ? (
              <div style={{ color: MUTED, fontSize: 12 }}>
                No standup updates in the current window.
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.standupHighlights.map((h, i) => (
                  <li key={i} style={{ padding: '3px 0' }}>
                    {h}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Capacity footnote */}
      <div style={{ marginTop: 18, ...card, background: '#f9fafb' }}>
        <span style={{ color: MUTED }}>Team capacity:</span>{' '}
        <b>{data.teamCapacityDays}d</b> total
        {data.memberCount > 0 && <> across {data.memberCount} engineer{data.memberCount === 1 ? '' : 's'}</>},{' '}
        <b>{data.remainingCapacityDays}d</b> remaining vs{' '}
        <b>{data.remainingEffortDays}d</b> of remaining work.
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 22,
          paddingTop: 10,
          borderTop: `1px solid ${LINE}`,
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: MUTED,
        }}
      >
        <span>Vector · Sprint Summary</span>
        <span>Generated {data.generatedAtLabel}</span>
      </div>
    </div>
  );
});

export default SprintReportDocument;
